import React, { useEffect, useState } from "react";
import { Icon } from "@iconify-icon/react";
import { uploadProfilePicture } from "../Service/ProfileService";
import LoadingSpinner from "../HelperComponents/LoadingSpinner";
import MessageTile from "../HelperComponents/MessageTile";

interface UploadProfilePictureProps {
  currentImageUrl?: string;
  onClose: () => void;
}

export const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50mb

export const UploadProfilePicture: React.FC<UploadProfilePictureProps> = ({
  currentImageUrl,
  onClose,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(
    currentImageUrl,
  );
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<
    "info" | "success" | "error" | "warning"
  >("info");
  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);

  useEffect(() => {
    setSelectedFile(null);
    setMessage(null);
    setPreviewUrl(currentImageUrl);
  }, [currentImageUrl]);

  const processFile = (file: File) => {
    if (!file.type.startsWith("image/")) {
      setMessageType("error");
      setMessage("Only image files are allowed.");
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setMessageType("error");
      setMessage("Only image files up to 20MB are allowed.");
      return;
    }

    setSelectedFile(file);
    setMessageType("info");
    setMessage(null);

    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    processFile(file);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(false);
    const file = event.dataTransfer.files?.[0];
    if (!file) {
      return;
    }
    processFile(file);
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile) {
      setMessageType("warning");
      setMessage("Please select an image to upload.");
      return;
    }

    try {
      setIsUploading(true);
      const reader = new FileReader();
      reader.readAsDataURL(selectedFile);
      reader.onloadend = async () => {
        const base64 = reader.result as string;
        const base64Image = base64.split(",")[1];
        setIsUploading(true);
        const response = await uploadProfilePicture(base64Image);
        setMessageType("success");
        setMessage(response.data.message);
        if (response.status === 200) {
          setIsUploading(false);
          setTimeout(() => {
            onClose();
          }, 1000);
        }
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to upload profile picture", error);
      setMessageType("error");
      setMessage("Something went wrong while uploading. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const dropzoneBorderColor =
    messageType === "error"
      ? "hsl(var(--error))" // red
      : isDragActive
        ? "hsl(var(--primary))"
        : selectedFile
          ? "hsl(var(--accent))" // accent
          : "#c8c6c4"; // neutral

  return (
    <div className="confirm-dialog-overlay" role="dialog" aria-modal="true">
      <div className="confirm-dialog">
        {message && (
          <MessageTile
            isVisible={!!message}
            message={message}
            type={messageType}
          />
        )}

        <div className="confirm-dialog-header">
          <div className="confirm-dialog-title">Change Profile Picture</div>

          <div className="confirm-dialog-message">
            Drag and drop an image, or browse to select a new profile photo.
          </div>
        </div>
        {isUploading && <LoadingSpinner />}
        {!isUploading && (
          <>
            <div
              className="confirm-dialog-content"
              style={{ textAlign: "center" }}
            >
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
                  width: 160,
                  height: 160,
                  borderRadius: "50%",
                  border: `3px dashed ${dropzoneBorderColor}`,
                  background: `hsl(var(--background))`,
                  textAlign: "center",
                  cursor: "pointer",
                  transition:
                    "border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease",
                  boxShadow: isDragActive
                    ? "0 0 0 3px rgba(0,120,212,0.25)"
                    : "0 4px 12px rgba(0,0,0,0.16)",
                  position: "relative",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px auto",
                }}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleInputChange}
                  style={{
                    position: "absolute",
                    inset: 0,
                    opacity: 0,
                    cursor: "pointer",
                  }}
                />

                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Selected profile preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <Icon
                      icon="line-md:cloud-alt-upload-filled-loop"
                      style={{ fontSize: 52, color: `hsl(var(--primary))` }}
                    />
                  </div>
                )}
              </div>

              <p
                style={{
                  fontSize: 13,
                  color: "hsl(var(--foreground))",
                  marginBottom: 8,
                }}
              >
                Drag & drop or click to select (JPG, PNG, GIF up to 50 MB).
              </p>

              {selectedFile && messageType !== "error" && (
                <p style={{ fontSize: 12, color: "#605e5c", marginBottom: 4 }}>
                  Selected file: {selectedFile.name}
                </p>
              )}

              {messageType === "error" && (
                <p style={{ fontSize: 12, color: "#a80000", fontWeight: 500 }}>
                  {message}
                </p>
              )}
            </div>
            <div className="confirm-dialog-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={onClose}
                disabled={isUploading}
                style={{ minWidth: "10vw" }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleConfirmUpload}
                disabled={isUploading}
                style={{ minWidth: "10vw" }}
              >
                {isUploading ? "Uploading..." : "Upload"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UploadProfilePicture;
